import { useEffect } from 'react';
import useMount from 'react-use/esm/useMount';
import usePrevious from 'react-use/esm/usePrevious';
import { NetworkStatus } from '@apollo/client';
import analytics from 'modules/analytics';
import type { UserInfo } from 'modules/analytics/analytics';
import { CUSTOM_TIMINGS, workspace } from 'modules/analytics/constants';
import { useCurrentCaseId } from 'hooks/useCurrentCase';
import { useLocation } from 'react-router-dom';
import { resetImagingProviderTrackers } from 'modules/viewer/workers/PixelDataSharedWorker';
import { useStudies } from 'domains/viewer/Viewer/StudyLoader/useStudies';
import { WithApolloContext } from 'modules/Apollo/types';
import type { GetMeQuery } from 'generated/graphql';
import { useCurrentUser } from './useCurrentUser';
import { useIsAuthenticated } from './useAuth';

export const useAnalytics = ({ context }: WithApolloContext) => {
  const { data, networkStatus, refetch } = useCurrentUser({ context });
  const isAuthenticated = useIsAuthenticated();
  const wasAuthenticated = usePrevious(isAuthenticated);
  const user = data?.me;

  useMount(() => {
    analytics.timing(CUSTOM_TIMINGS.reactAppRendered);
  });

  // Reset the timing metrics when the user navigates to a new page
  const location = useLocation();
  const previousLocation = usePrevious(location);
  useEffect(() => {
    if (location.pathname !== previousLocation?.pathname) {
      analytics.resetTimings();
      resetImagingProviderTrackers();
    }
  }, [location, previousLocation]);

  useEffect(() => {
    if (isAuthenticated && wasAuthenticated === false) void refetch();
  }, [isAuthenticated, refetch, wasAuthenticated]);

  useEffect(() => {
    const analyticsUser = toAnalyticsUser(user);

    if (analyticsUser != null && networkStatus === NetworkStatus.ready) {
      const segmentData = {
        reporterSettings: user?.reporterSettings,
      } as const;
      analytics.identify(analyticsUser, { segmentData });
    }
  }, [networkStatus, user]);

  useEffect(() => {
    const onlineAction = () => {
      analytics.track(workspace.sys.system, { type: 'online' });
    };
    const offlineAction = () => {
      analytics.track(workspace.sys.system, { type: 'offline' });
    };

    const pageVisibility = (_: any) => {
      analytics.track(workspace.sys.system, {
        type: 'page_visibility',
        visible: !document.hidden,
      });
    };

    window.addEventListener('visibilitychange', pageVisibility);
    window.addEventListener('online', onlineAction);
    window.addEventListener('offline', offlineAction);

    return () => {
      window.removeEventListener('online', onlineAction);
      window.removeEventListener('offline', offlineAction);
      window.removeEventListener('visibilitychange', pageVisibility);
    };
  }, []);

  return;
};

function toAnalyticsUser(user?: GetMeQuery['me']): UserInfo | null {
  if (user == null || user.clinic == null) return null;

  return {
    id: user.id,
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.email,
    clinic: {
      name: user.clinic.name,
      smid: user.clinic.smid,
    },
  };
}

export const useCaseAnalytics = ({ context }: WithApolloContext) => {
  const currentCaseId = useCurrentCaseId();
  const previousCaseId = usePrevious(currentCaseId);

  useEffect(() => {
    if (previousCaseId != null && previousCaseId !== currentCaseId) {
      // Reset the timestamp of the page load time so we can accurately
      // measure metrics relative to the time a study was requested to load.
      analytics.resetLoadEventStart();
    }
  }, [previousCaseId, currentCaseId]);

  const { priorStudiesIds, autoloadedStudiesIds } = useStudies({ context });

  useEffect(() => {
    analytics.updateReadSession({
      priorStudiesSmids: priorStudiesIds,
      autoloadedStudiesSmids: autoloadedStudiesIds,
    });
  });
};
